import { useState } from 'react';
import { Badge } from '@/shared/components/ui/Badge';
import type { FrameReplenishmentItem } from '@/shared/types/domain.types';

interface OrderingTableProps {
  frames: FrameReplenishmentItem[];
  selectedFrameIds: string[];
  onSelectionChange: (frameIds: string[]) => void;
}

type SortField = 'reorder_entry_date' | 'brand' | 'frame_name_or_model' | 'location';
type SortDirection = 'asc' | 'desc';

export function OrderingTable({
  frames,
  selectedFrameIds,
  onSelectionChange,
}: OrderingTableProps) {
  const [sortField, setSortField] = useState<SortField>('reorder_entry_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const allSelected = frames.length > 0 && frames.every((f) => selectedFrameIds.includes(f.id));
  const someSelected = frames.some((f) => selectedFrameIds.includes(f.id)) && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all
      const remaining = selectedFrameIds.filter((id) => !frames.find((f) => f.id === id));
      onSelectionChange(remaining);
    } else {
      // Select all
      const allIds = [...new Set([...selectedFrameIds, ...frames.map((f) => f.id)])];
      onSelectionChange(allIds);
    }
  };

  const handleSelectFrame = (frameId: string) => {
    if (selectedFrameIds.includes(frameId)) {
      onSelectionChange(selectedFrameIds.filter((id) => id !== frameId));
    } else {
      onSelectionChange([...selectedFrameIds, frameId]);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedFrames = [...frames].sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;

    switch (sortField) {
      case 'reorder_entry_date':
        aVal = new Date(a.reorder_entry_date).getTime();
        bVal = new Date(b.reorder_entry_date).getTime();
        break;
      case 'brand':
        aVal = a.brand.toLowerCase();
        bVal = b.brand.toLowerCase();
        break;
      case 'frame_name_or_model':
        aVal = a.frame_name_or_model.toLowerCase();
        bVal = b.frame_name_or_model.toLowerCase();
        break;
      case 'location':
        aVal = a.location?.name.toLowerCase() || '';
        bVal = b.location?.name.toLowerCase() || '';
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="text-gray-400">⇅</span>;
    return <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  if (frames.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No frames available for this manufacturer.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="w-12 px-3 py-3">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => el && (el.indeterminate = someSelected)}
                onChange={handleSelectAll}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('location')}
            >
              <div className="flex items-center gap-1">
                Location <SortIcon field="location" />
              </div>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('brand')}
            >
              <div className="flex items-center gap-1">
                Brand <SortIcon field="brand" />
              </div>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('frame_name_or_model')}
            >
              <div className="flex items-center gap-1">
                <span className="hidden lg:inline">Frame Name/Model</span>
                <span className="lg:hidden">Frame</span>
                <SortIcon field="frame_name_or_model" />
              </div>
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Color
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Size
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              <span className="hidden md:inline">Order Type</span>
              <span className="md:hidden">Type</span>
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              <span className="hidden md:inline">Patient / Tray</span>
              <span className="md:hidden">P / T</span>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('reorder_entry_date')}
            >
              <div className="flex items-center gap-1">
                Entry Date <SortIcon field="reorder_entry_date" />
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedFrames.map((frame) => (
            <tr
              key={frame.id}
              className={`hover:bg-gray-50 ${selectedFrameIds.includes(frame.id) ? 'bg-blue-50' : ''}`}
            >
              <td className="px-3 py-4 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={selectedFrameIds.includes(frame.id)}
                  onChange={() => handleSelectFrame(frame.id)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                <span className="hidden lg:inline">{frame.location?.name || 'Unknown'}</span>
                <span className="lg:hidden">{frame.location?.short_code || frame.location?.name || 'Unknown'}</span>
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                {frame.brand}
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                {frame.frame_name_or_model}
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                {frame.color}
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                {frame.eye_size}
              </td>
              <td className="px-3 py-4 whitespace-nowrap">
                <Badge variant={frame.order_type === 'STOCK' ? 'blue' : 'purple'} size="sm">
                  {frame.order_type}
                </Badge>
              </td>
              <td className="px-3 py-4 text-sm text-gray-900">
                {frame.order_type === 'PATIENT' && frame.patient_name && (
                  <div>
                    <div className="font-medium">{frame.patient_name}</div>
                    {frame.tray_number && (
                      <div className="text-xs text-gray-500">Tray: {frame.tray_number}</div>
                    )}
                  </div>
                )}
                {frame.order_type === 'STOCK' && (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(frame.reorder_entry_date).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
